// Shapes the two analytics endpoints into what the cards render.
//
// GET .../conversations/timeseries  daily counts
// GET .../conversations/topics      topics with share percentages
//
// Neither response could be inspected against a live session, so each field is
// read through a short list of likely names. Once the real payload is known
// this file is the only place that needs narrowing.

import React from "react";
import {
  DollarIcon,
  StarIcon,
  PuzzleIcon,
  MonitorIcon,
  CardIcon,
  DotsIcon,
} from "./icons";

const firstOf = (source, keys) => {
  if (!source) return undefined;

  for (const key of keys) {
    const value = source[key];
    if (value !== undefined && value !== null) return value;
  }

  return undefined;
};

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

// "2026-09-04" -> "4 Sep". Parsed as parts rather than through Date, which
// reads a bare date string as UTC and can shift it a day west of GMT.
const shortDate = (value) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ""));
  if (!match) return String(value || "");

  const [, year, month, day] = match;
  return new Date(Number(year), Number(month) - 1, Number(day)).toLocaleDateString(
    undefined,
    { day: "numeric", month: "short" },
  );
};

/**
 * @param list the timeseries rows
 * @returns {Array<{date: string, label: string, value: number}>}
 */
export const readSeries = (list) =>
  (Array.isArray(list) ? list : []).map((point) => {
    const date = firstOf(point, ["date", "day", "bucket", "label"]);

    const raw = point?.bySource || point?.sources || {};

    // Whatever channels the response names, not a fixed three: the API has
    // already dropped one, and hardcoding the list puts a channel in the
    // legend that no longer exists.
    const bySource = Object.keys(raw).reduce((acc, key) => {
      acc[key] = toNumber(raw[key]);
      return acc;
    }, {});

    return {
      date: String(date || ""),
      label: shortDate(date),
      value: toNumber(
        firstOf(point, ["count", "conversations", "total", "value"]),
      ),
      bySource,
      leads: toNumber(firstOf(point, ["leads", "leadCount"])),
    };
  });

// The card's icon and colour per topic. Matched on keywords because the topics
// themselves come from an LLM and aren't a fixed set.
const ICON_RULES = [
  { test: /pric|plan|cost|quote/i, icon: <DollarIcon />, variant: "indigo" },
  { test: /feature|product|capab/i, icon: <StarIcon />, variant: "amber" },
  { test: /integrat|api|connect/i, icon: <PuzzleIcon />, variant: "blue" },
  { test: /demo|trial|onboard/i, icon: <MonitorIcon />, variant: "green" },
  { test: /billing|account|invoice|payment/i, icon: <CardIcon />, variant: "rose" },
];

const decorate = (label) => {
  const rule = ICON_RULES.find((entry) => entry.test.test(label));
  // Only the presentation, not the matcher that found it.
  if (rule) return { icon: rule.icon, variant: rule.variant };

  return { icon: <DotsIcon />, variant: "muted" };
};

/**
 * @param list the topics rows
 * @returns {Array<{label: string, percent: number, icon: JSX.Element, variant: string}>}
 */
// Topics come back as bare lowercase words ("plan", "help"), which read as a
// typo in a heading position.
const titleCase = (value) =>
  String(value)
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

export const readTopics = (list) =>
  (Array.isArray(list) ? list : []).map((topic) => {
    const label =
      firstOf(topic, ["topic", "label", "name", "title"]) || "Other";
    const raw = toNumber(
      firstOf(topic, ["percentage", "percent", "share", "value"]),
    );
    // Accepts either 0.32 or 32.
    const percent = raw > 0 && raw <= 1 ? raw * 100 : raw;

    return {
      label: titleCase(label),
      percent,
      // Rounding 5.2 to 5 throws away the only thing separating the long tail
      // of small topics, so one decimal is kept when there is one.
      percentLabel: Number.isInteger(percent)
        ? String(percent)
        : percent.toFixed(1),
      count: toNumber(firstOf(topic, ["count", "conversations", "total"])),
      ...decorate(String(label)),
    };
  });
